import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Conway’s game of life in Google Docs",
  "description": "",
  "published": "2016-08-16T00:00:00.000Z",
  "redirect_from": ["/blog/conways-game-of-life-in-google-docs/swizec/6931"],
  "categories": "Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/08/glider.gif",
        "alt": "Conway glider"
      }}></img></p>
    <p>{`Why? Because.`}</p>
    <p>{`Have you really never wanted to create `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Conway%27s_Game_of_Life"
      }}>{`Conway’s game of life`}</a>{` in a google doc spreadsheet? Really, never? Okay, it’s a pretty weird thing to do. And it’s not as fun as I hoped.`}</p>
    <p>{`You can play with it, `}<a parentName="p" {...{
        "href": "https://docs.google.com/spreadsheets/d/1k8mT9a-Fu8x1TkvjYGtrDng8vWQAnNf01nSFCaVwqO4/edit?usp=sharing"
      }}>{`here`}</a></p>
    <p>{`I’m giving a 30min presentation on “`}<em parentName="p">{`How to JavaScript`}</em>{`” to our not-engineers – the operations and business development teams. But how? It’s not like you can teach somebody JavaScript from scratch in 30 minutes. The most you can do is show them enough to want to learn more.`}</p>
    <p>{`That’s easier, but still … how? You can’t just show them a bunch of for loops and functions and hope it sticks. You have to get them `}<em parentName="p">{`excited`}</em>{`.`}</p>
    <p>{`The answer I’m trying to answer is: `}<em parentName="p">{`“How can this make my life better?”`}</em></p>
    <p>{`They all spend a lot of time in Google Docs. They run a lot of day to day processes in there. Google Docs has scripting capabilities.`}</p>
    <p>{`See where I’m getting at?`}</p>
    <p>{`Conway’s Game of Life inside Google Docs, of course. It highlights the basics of reading and manipulating properties of individual cells in spreadsheets, and the game itself is easy to explain:`}</p>
    <ol>
      <li parentName="ol">{`Any live cell with fewer than two live neighbours dies, as if caused by under-population.`}</li>
      <li parentName="ol">{`Any live cell with two or three live neighbours lives on to the next generation.`}</li>
      <li parentName="ol">{`Any live cell with more than three live neighbours dies, as if by over-population.`}</li>
      <li parentName="ol">{`Any dead cell with exactly three live neighbours becomes a live cell, as if by reproduction.`}</li>
    </ol>
    <p><img parentName="p" {...{
        "src": "/8267036dcf6ae3d5150a797592720639/glidergif56df35.gif",
        "alt": null
      }}></img></p>
    <p><img parentName="p" {...{
        "src": "/e9faa09efc6c34b9c165a05051f118df/linkergif395aig.gif",
        "alt": null
      }}></img></p>
    <p><img parentName="p" {...{
        "src": "/838a5cc66e0456d2c55991b92c649846/wikipedia-commons-0-07-Game_of_life_pulsar.gif",
        "alt": null
      }}></img></p>
    <p>{`I showed one of them some gifs and he said `}<em parentName="p">{`“Oh dude, I’m excited! That looks so cool”`}</em>{`. All the confirmation I needed to go waste 3 hours of my life figuring out how to build this! ?`}</p>
    <p>{`Scripting Google Docs is hell.`}</p>
    <p>{`You can’t alert, there’s no console.log, everything is slow, and ES6 works only partially. You get destructuring, but no string templates. I dared not try arrow functions.`}</p>
    <p>{`Timers also don’t exist. Which means you have to manually step through the game loop by clicking a button. ?`}</p>
    <p>{`One silver lining is `}<a parentName="p" {...{
        "href": "https://github.com/peterherrmann/BetterLog"
      }}>{`this 3rd party logger called BetterLogger`}</a>{`, which lets you `}<inlineCode parentName="p">{`Logger.log`}</inlineCode>{` things to a separate sheet in your spreadsheet. It’s not very smart, but it helps a lot.`}</p>
    <p>{`And if you thought DOM was slow, wait ’till you try Docs access. Naively searching through 30x30 cells and counting neighbors took `}<em parentName="p">{`many seconds`}</em>{`. Maybe 20 seconds per game tick.`}</p>
    <p>{`That can be optimized, though. Just access the spreadsheet less.`}</p>
    <p>{`The first step is to set global vars and add a menu:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// global vars
var maxX = 30,
  maxY = 30,
  Alive = "#000000";

// almost everything needs a reference to this
var Sheet = SpreadsheetApp.getActiveSheet();

// add menu
function onOpen() {
  var spreadsheet = SpreadsheetApp.getActiveSpreadsheet();
  var menuItems = [
    { name: "Step game", functionName: "stepGame" },
    { name: "Clear game", functionName: "clearGame" },
  ];
  spreadsheet.addMenu("Conway's game of life", menuItems);
}
`}</code></pre>
    <p>{`Docs runs this code when you open your document. It adds a menu that looks like this:`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/08/menu.png",
        "alt": "Menu"
      }}></img></p>
    <p><inlineCode parentName="p">{`Step Game`}</inlineCode>{` runs an iteration of the game loop, `}<inlineCode parentName="p">{`Clear Game`}</inlineCode>{` clears all cells. Black background means a living cell and white background means a not-living cell.`}</p>
    <p>{`On each game step, we build a map of living cells, then run the game rules on each. Like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function stepGame() {
  var range = Sheet.getRange(1, 1, maxX, maxY);

  var numRows = range.getNumRows(),
    numCols = range.getNumColumns();

  var lifeMap = [];

  for (var y = 1; y <= numRows; y++) {
    lifeMap[y] = [];

    for (var x = 1; x <= numCols; x++) {
      lifeMap[y][x] = range.getCell(y, x).getBackground() == Alive;
    }
  }

  for (var y = 1; y <= numRows; y++) {
    for (var x = 1; x <= numCols; x++) {
      conwayGame(lifeMap, y, x);
    }
  }
}
`}</code></pre>
    <p>{`You can think of the spreadsheet as 2-dimensional memory. First dimension is the row, second is the column. You can store styling information, the value itself, and notes. Docs lets you access directly on both dimension, but it’s slow.`}</p>
    <p>{`Having 2D memory also means there will be a lot of these nested for loop constructs in your code. You’re limited to O(n2) algorithms at best.`}</p>
    <p>{`Applying Conway rules to each cell looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function conwayGame(lifeMap, y, x) {
  var neighborPos = [
    y > 1 ? y - 1 : 1,
    x > 1 ? x - 1 : 1,
    y < maxY ? y + 1 : maxY,
    x < maxX ? x + 1 : maxX,
  ];

  var livingNeighbors = countLife(lifeMap, neighborPos);

  if (lifeMap[y][x]) {
    livingNeighbors -= 1;
  }

  if ((livingNeighbors < 2 || livingNeighbors > 3) && lifeMap[y][x]) {
    deactivate(Sheet.getRange(y, x));
  } else if (livingNeighbors == 3) {
    activate(Sheet.getRange(y, x));
  }
}
`}</code></pre>
    <p>{`We count living neighbors, discount the current cell if it’s alive, then decide whether to `}<inlineCode parentName="p">{`deactivate`}</inlineCode>{` or `}<inlineCode parentName="p">{`activate`}</inlineCode>{` it. Counting the neighbors literally means walking through a 3x3 array around the current cell and counting cells that have a black background.`}</p>
    <p>{`Activating or deactivating is a call to `}<inlineCode parentName="p">{`cell.setBackground('black')`}</inlineCode>{` and `}<inlineCode parentName="p">{`cell.setBackground('white')`}</inlineCode>{`.`}</p>
    <p>{`That’s kind of it … I’m embarrassed it took me 3 hours …`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      